/**
 * Assign the Client Accounts a user looks after.
 *
 * This is the screen behind the "Account Managers can't create plans" bug. An [ACCT]
 * user's permissions are all scoped to "their Accounts", resolved through the
 * account_owner table — and until this existed nothing could write to it, so every
 * Account Manager owned nothing and every scoped action was refused.
 *
 * The CRM suggestions are suggestions. `crm_csm_name` / `crm_client_partner_name` are
 * free-text names synced from the CRM, not user ids, so a match is a hint an admin
 * confirms — never an automatic grant.
 */

import { Building2, Check, Plus, X } from "lucide-react";
import { useMemo, useState } from "react";
import { EmptyState } from "@/components/shared/empty-state";
import { SearchableSelect } from "@/components/shared/SearchableSelect";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { useAccounts } from "@/hooks/use-accounts";
import type { UserRow } from "@/hooks/use-users";
import {
  useGrantAccountOwnership,
  useOwnedAccounts,
  useOwnershipSuggestions,
  useRevokeAccountOwnership,
} from "@/hooks/use-users";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: UserRow;
}

export function ManageAccountOwnershipDialog({ open, onOpenChange, user }: Props) {
  const [pendingAccountId, setPendingAccountId] = useState("");

  const userId = open ? user.id : null;
  const owned = useOwnedAccounts(userId);
  const suggestions = useOwnershipSuggestions(userId);
  const accounts = useAccounts({ limit: 100 });

  const grant = useGrantAccountOwnership();
  const revoke = useRevokeAccountOwnership();

  const ownedIds = useMemo(
    () => new Set((owned.data ?? []).map((row) => row.clientAccountId)),
    [owned.data],
  );

  const options = useMemo(
    () =>
      (accounts.data?.data ?? [])
        .filter((account) => !ownedIds.has(account.id))
        .map((account) => ({ value: account.id, label: account.name })),
    [accounts.data, ownedIds],
  );

  function handleGrant(clientAccountId: string) {
    if (!clientAccountId) return;
    grant.mutate(
      { userId: user.id, clientAccountId },
      { onSuccess: () => setPendingAccountId("") },
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Accounts for {user.name}</DialogTitle>
          <DialogDescription>
            {user.role === "ACCT"
              ? "An Account Manager can only see and plan for the Accounts listed here."
              : "Which Client Accounts this person looks after."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <SearchableSelect
                options={options}
                value={pendingAccountId}
                onValueChange={setPendingAccountId}
                placeholder="Find an Account…"
                emptyMessage="No matching Account"
                loading={accounts.isLoading}
              />
            </div>
            <Button
              onClick={() => handleGrant(pendingAccountId)}
              disabled={!pendingAccountId || grant.isPending}
            >
              <Plus className="mr-2 h-4 w-4" />
              Add
            </Button>
          </div>

          <div className="space-y-2">
            <p className="text-sm font-medium">Assigned</p>
            {owned.isLoading ? (
              <Skeleton className="h-20 w-full" />
            ) : (owned.data ?? []).length === 0 ? (
              <EmptyState
                icon={Building2}
                title="No Accounts yet"
                description={
                  user.role === "ACCT"
                    ? "Until an Account is assigned, this Account Manager cannot open or plan for anything."
                    : "Nothing assigned."
                }
              />
            ) : (
              <ul className="divide-y divide-border rounded-md border border-border">
                {(owned.data ?? []).map((row) => (
                  <li
                    key={row.ownershipId}
                    className="flex items-center justify-between gap-3 px-3 py-2"
                  >
                    <span className="text-sm">{row.accountName}</span>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={`Remove ${row.accountName}`}
                      disabled={revoke.isPending}
                      onClick={() =>
                        revoke.mutate({ userId: user.id, clientAccountId: row.clientAccountId })
                      }
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {(suggestions.data ?? []).length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-medium">Suggested from the CRM</p>
              <p className="text-xs text-muted-foreground">
                These Accounts name {user.name} as their CSM or Client Partner. Confirm before
                assigning — the CRM stores a name, not a person.
              </p>
              <ul className="divide-y divide-border rounded-md border border-border">
                {(suggestions.data ?? []).map((row) => (
                  <li
                    key={row.clientAccountId}
                    className="flex items-center justify-between gap-3 px-3 py-2"
                  >
                    <span className="text-sm">
                      {row.accountName}
                      <span className="ml-2 text-xs text-muted-foreground">
                        {row.matchedField === "crmCsmName" ? "CSM" : "Client Partner"}
                      </span>
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={grant.isPending}
                      onClick={() => handleGrant(row.clientAccountId)}
                    >
                      <Check className="mr-2 h-4 w-4" />
                      Assign
                    </Button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
